import React from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Button, Chip, useTheme,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import FlagIcon from '@mui/icons-material/Flag';
import { tokens } from '../../../../../theme';
import { useTestSession } from '../TestSessionContext';

const ReviewScreen = ({ open, onClose, onConfirmSubmit, sections, isAdmin = false }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { answers, setCurrentSectionIndex, setCurrentQuestionIndex, submitting, choiceGroupSelections } = useTestSession();
    const [confirmOpen, setConfirmOpen] = React.useState(false);

    const handleJumpTo = (sectionIdx, questionIdx) => {
        setCurrentSectionIndex(sectionIdx);
        setCurrentQuestionIndex(questionIdx);
        onClose();
    };

    const allQuestions = sections.flatMap(s => s.v2_test_questions || []);
    const answeredCount = allQuestions.filter(q => {
        // Skip questions hidden by choice group
        if (q.choice_group) {
            const selected = choiceGroupSelections[q.choice_group];
            if (selected !== undefined && selected !== q.id) return false;
        }
        const a = answers.get(q.id);
        return a?.answer_text?.trim();
    }).length;
    const flaggedCount = allQuestions.filter(q => answers.get(q.id)?.flagged).length;
    const totalActive = allQuestions.filter(q => {
        if (!q.choice_group) return true;
        const selected = choiceGroupSelections[q.choice_group];
        return selected === undefined || selected === q.id;
    }).length;

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>Review & Submit</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                        <Chip
                            icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                            label={`${answeredCount} of ${totalActive} answered`}
                            variant="outlined"
                        />
                        {totalActive - answeredCount > 0 && (
                            <Chip
                                icon={<RemoveCircleOutlineIcon sx={{ fontSize: 16, color: 'error.main !important' }} />}
                                label={`${totalActive - answeredCount} unanswered`}
                                variant="outlined"
                            />
                        )}
                        {flaggedCount > 0 && (
                            <Chip
                                icon={<FlagIcon sx={{ color: '#f59e0b !important' }} />}
                                label={`${flaggedCount} flagged`}
                                variant="outlined"
                                sx={{ borderColor: '#f59e0b', color: '#f59e0b' }}
                            />
                        )}
                    </Box>

                    {sections.map((section, sIdx) => {
                        const questions = section.v2_test_questions || [];
                        return (
                            <Box key={section.id || sIdx} sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                                    {section.section_name || `Section ${sIdx + 1}`}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {questions.map((q, qIdx) => {
                                        // Skip hidden choice group questions
                                        if (q.choice_group) {
                                            const selected = choiceGroupSelections[q.choice_group];
                                            if (selected !== undefined && selected !== q.id) return null;
                                        }
                                        const a = answers.get(q.id);
                                        const isAnswered = a?.answer_text?.trim();
                                        const isFlagged = a?.flagged;

                                        return (
                                            <Chip
                                                key={q.id}
                                                label={`Q${q.question_number}`}
                                                size="small"
                                                icon={
                                                    isFlagged ? <FlagIcon sx={{ fontSize: 16, color: '#f59e0b !important' }} /> :
                                                    isAnswered ? <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> :
                                                    <RemoveCircleOutlineIcon sx={{ fontSize: 16, color: 'error.main !important' }} />
                                                }
                                                variant="outlined"
                                                onClick={() => handleJumpTo(sIdx, qIdx)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    borderColor: isAnswered ? 'success.main' : 'error.main',
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            </Box>
                        );
                    })}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Back to Test</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => setConfirmOpen(true)}
                        disabled={submitting}
                    >
                        Submit Test
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation dialog */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Confirm Submission</DialogTitle>
                <DialogContent>
                    {answeredCount < totalActive && (
                        <Typography mb={1}>
                            You have <b>{totalActive - answeredCount} unanswered question{totalActive - answeredCount !== 1 ? 's' : ''}</b>.
                        </Typography>
                    )}
                    {isAdmin ? (
                        <Typography>Choose how to submit on behalf of this student:</Typography>
                    ) : (
                        <Typography>Are you sure you want to submit?</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    {isAdmin ? (
                        <>
                            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                            <Button variant="outlined" onClick={() => { setConfirmOpen(false); onConfirmSubmit('none'); }} disabled={submitting}>
                                No Grading
                            </Button>
                            <Button variant="outlined" onClick={() => { setConfirmOpen(false); onConfirmSubmit('delayed'); }} disabled={submitting}>
                                Grade (3hr)
                            </Button>
                            <Button variant="contained" color="primary" onClick={() => { setConfirmOpen(false); onConfirmSubmit('immediate'); }} disabled={submitting}>
                                Grade Now
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                            <Button variant="contained" color="error"
                                onClick={() => { setConfirmOpen(false); onConfirmSubmit(); }}
                                disabled={submitting}
                            >
                                {submitting ? 'Submitting...' : 'Yes, Submit'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ReviewScreen;
